const namespaced = true
import {PDFDocument, StandardFonts, rgb} from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';


const state = {}

const actions = {
    async fetchImageAsBase64({}, url) {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to fetch image: ${response.status}`);
        }

        const blob = await response.blob();
        const mimeType = blob.type; // e.g. "image/png"

        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result); // includes proper data URL
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    },

    estimateTextHeight(context, {text, font, fontSize, maxWidth}) {
        const container = document.createElement('div');
        container.innerHTML = text;

        const segments = [];

        function parseNode(node, style = {}) {
            if (node.nodeType === 3) {
                segments.push({text: node.textContent, style});
            } else if (node.nodeType === 1) {
                const newStyle = {...style};
                if (node.tagName === 'B' || node.tagName === 'STRONG') newStyle.bold = true;
                if (node.tagName === 'I' || node.tagName === 'EM') newStyle.italic = true;
                node.childNodes.forEach(child => parseNode(child, newStyle));
            }
        }

        container.childNodes.forEach(node => parseNode(node));

        let lineWidth = 0;
        let lineCount = 1;

        for (const segment of segments) {
            const words = segment.text.split(/\s+/);
            for (let word of words) {
                if (!word) continue;
                word = word.replace(/\u202F/g, ' ');
                const wordWidth = font.widthOfTextAtSize(word + ' ', fontSize);
                if (lineWidth + wordWidth > maxWidth) {
                    lineCount++;
                    lineWidth = 0;
                }
                lineWidth += wordWidth;
            }
        }

        return lineCount * (fontSize + 2); // approximate line height
    },

    ensureSpace(context, {currentY, requiredHeight, pageHeight, minMargin = 50, pdfDoc}) {
        const headerHeight = 60; // Must match actual header
        const usableHeight = pageHeight - headerHeight - minMargin;

        if (currentY - requiredHeight < minMargin) {
            const newPage = pdfDoc.addPage();
            return {page: newPage, y: usableHeight};
        }
        return null;
    },

    async drawFormattedHtml(context, {text, x, y, maxWidth, font, page, size = 12, color = rgb(0, 0, 0)}) {
        const container = document.createElement('div');
        container.innerHTML = text;

        const segments = [];

        function parseNode(node, style = {}) {
            if (node.nodeType === 3) {
                segments.push({text: node.textContent, style});
            } else if (node.nodeType === 1) {
                const newStyle = {...style};
                if (node.tagName === 'B' || node.tagName === 'STRONG') newStyle.bold = true;
                if (node.tagName === 'I' || node.tagName === 'EM') newStyle.italic = true;
                if (node.tagName === 'A') {
                    newStyle.color = rgb(0, 0, 1);
                    newStyle.href = node.getAttribute('href');
                }
                node.childNodes.forEach(child => parseNode(child, newStyle));
            }
        }

        container.childNodes.forEach(node => parseNode(node));

        let cursorX = x;
        let cursorY = y;

        for (const segment of segments) {
            const words = segment.text.split(/\s+/);

            for (let word of words) {
                if (!word) continue;
                word = word.replace(/\u202F/g, ' '); // Replace narrow no-break space
                const wordWidth = font.widthOfTextAtSize(word + ' ', size);
                if (cursorX + wordWidth > x + maxWidth) {
                    cursorY -= size + 2;
                    cursorX = x;
                }

                page.drawText(word + ' ', {
                    x: cursorX,
                    y: cursorY,
                    size,
                    font,
                    color: segment.style?.color || color,
                });

                // Add link annotation if href present
                if (segment.style?.href) {
                    const linkRect = [
                        cursorX,
                        cursorY,
                        cursorX + wordWidth,
                        cursorY + size + 2, // approximate height
                    ];

                    const pdfPage = page.node;

                    const linkAnnotation = page.doc.context.obj({
                        Type: 'Annot',
                        Subtype: 'Link',
                        Rect: linkRect,
                        Border: [0, 0, 0],
                        A: {
                            Type: 'Action',
                            S: 'URI',
                            URI: segment.style.href,
                        },
                    });

                    pdfPage.addAnnot(linkAnnotation);
                }

                cursorX += wordWidth;
            }
        }

        return cursorY - size;
    },

    //prior version without handling anchor tags correctly
    async drawFormattedHtml2(context, {text, x, y, maxWidth, font, page, size = 12, color = rgb(0, 0, 0)}) {
        const container = document.createElement('div');
        container.innerHTML = text;

        const segments = [];

        function parseNode(node, style = {}) {
            if (node.nodeType === 3) {
                segments.push({text: node.textContent, style});
            } else if (node.nodeType === 1) {
                const newStyle = {...style};
                if (node.tagName === 'B' || node.tagName === 'STRONG') newStyle.bold = true;
                if (node.tagName === 'I' || node.tagName === 'EM') newStyle.italic = true;
                if (node.tagName === 'A') newStyle.color = rgb(0, 0, 1);
                node.childNodes.forEach(child => parseNode(child, newStyle));
            }
        }

        container.childNodes.forEach(node => parseNode(node));

        let cursorX = x;
        let cursorY = y;

        for (const segment of segments) {
            const words = segment.text.split(/\s+/);
            for (let word of words) {
                if (!word) continue;
                word = word.replace(/\u202F/g, ' '); // Replace narrow no-break space
                const wordWidth = font.widthOfTextAtSize(word + ' ', size);
                if (cursorX + wordWidth > x + maxWidth) {
                    cursorY -= size + 2;
                    cursorX = x;
                }

                page.drawText(word + ' ', {
                    x: cursorX,
                    y: cursorY,
                    size,
                    font,
                    color: segment.style?.color || color,
                });

                cursorX += wordWidth;
            }
        }

        return cursorY - size;
    },

    wrapText(context, {text, font, fontSize, maxWidth}) {
        const words = text.split(' ');
        const lines = [];
        let currentLine = '';

        for (const word of words) {
            const testLine = currentLine ? `${currentLine} ${word}` : word;
            const width = font.widthOfTextAtSize(testLine, fontSize);
            if (width <= maxWidth) {
                currentLine = testLine;
            } else {
                if (currentLine) lines.push(currentLine);
                currentLine = word;
            }
        }

        if (currentLine) lines.push(currentLine);
        return lines;
    },

    async groupEventsByDate(context, events) {
        const grouped = {};

        for (const event of events) {
            let session = Object.hasOwn(event, 'display_type') ? event : event.session;
            const date = new Date(session.start_time).toISOString().split('T')[0]; // "YYYY-MM-DD"

            if (!grouped[date]) {
                grouped[date] = [];
            }

            grouped[date].push(event);
        }

        return grouped;
    },


    async generatePDF({dispatch}, {title, persons, events, industryList}) {
        const pdfDoc = await PDFDocument.create();
        // Use built-in standard fonts instead of custom fonts
        const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
        const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
        const fontItalic = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);
        const page = pdfDoc.addPage();
        const {width, height} = page.getSize();
        const headerHeight = 60;
        const topMargin = 20;

        // Optional logo - try to load, but continue if it fails
        let logoImage = null;
        let logoHeight = 0; // Declare outside try-catch so it's accessible later
        
        try {
            const logoUrl = '/assets/canvas-bg.png';
            const logoBytes = await fetch(logoUrl).then(res => {
                if (!res.ok) throw new Error('Logo not found');
                return res.arrayBuffer();
            });
            
            if (logoUrl.endsWith('.png')) {
                logoImage = await pdfDoc.embedPng(logoBytes);
            } else if (logoUrl.endsWith('.jpg') || logoUrl.endsWith('.jpeg')) {
                logoImage = await pdfDoc.embedJpg(logoBytes);
            }

            const logoWidth = 250;
            logoHeight = (logoImage.height / logoImage.width) * logoWidth;
            const logoX = (width - logoWidth) / 2;
            const logoY = height - logoHeight - 50;

            page.drawImage(logoImage, {
                x: logoX,
                y: logoY,
                width: logoWidth,
                height: logoHeight,
            });
        } catch (logoError) {
            console.warn('Logo not found, continuing without logo:', logoError.message);
        }

        const fontSize = 18;
        const text = title;

        const textWidth = font.widthOfTextAtSize(text, fontSize);
        const x = (width - textWidth) / 2;
        let y = height / 2 + fontSize / 2; // roughly vertically center baseline
        page.drawText(text, {x, y: y - logoHeight / 2, size: fontSize, font});

        y = height - headerHeight - topMargin;
        y -= 30;

        let currentPage = pdfDoc.addPage();
        const marginX = 50;
        //START PERSON BLOCK
        if (persons.length > 0) {
            for (const person of persons) {
                const imageWidth = 80;
                const imageHeight = 100;
                const gapBetweenCols = 20;
                const fontSize = 10;

                const leftColX = 50;
                let rightColX;
                let maxTextWidth;

                if (person.imageBase64) {
                    rightColX = leftColX + imageWidth + gapBetweenCols;
                    maxTextWidth = 450 - imageWidth - gapBetweenCols;
                } else {
                    rightColX = leftColX; // text starts at left margin
                    maxTextWidth = 450;   // full width available for text
                }

                // Estimate bio height using action
                const bioHeight = person.bio
                    ? await dispatch('estimateTextHeight', {text: person.bio, font, fontSize, maxWidth: maxTextWidth})
                    : 0;

                const textBlockHeight = 7 * (fontSize + 2); // fixed lines for person info
                const totalHeightNeeded = Math.max(textBlockHeight + bioHeight, imageHeight);

                const spaceResult = await dispatch('ensureSpace', {
                    currentY: y,
                    requiredHeight: totalHeightNeeded,
                    pageHeight: height,
                    pdfDoc,
                });

                if (spaceResult) {
                    currentPage = spaceResult.page;
                    y = spaceResult.y;
                }

                const startY = y;

                // Draw image
                if (person.imageBase64) {
                    let img;
                    const cleanBase64 = person.imageBase64.replace(/^data:.*?;base64,/, '');
                    try {
                        if (person.imageBase64.includes('/9j/')) {
                            img = await pdfDoc.embedJpg(cleanBase64);
                        } else if (person.imageBase64.includes('iVBOR')) {
                            img = await pdfDoc.embedPng(cleanBase64);
                        }
                        currentPage.drawImage(img, {
                            x: leftColX,
                            y: startY - imageHeight,
                            width: imageWidth,
                            height: imageHeight,
                        });
                    } catch (err) {
                        console.warn('Image failed to load:', err);
                    }
                }

                // Draw text info
                let textY = startY - fontSize;
                const lines = [
                    `${person.name}`,
                    person.phone ? `Phone: ${person.phone}` : null,
                    person.email ? `Email: ${person.email}` : null,
                    '',
                    person.institute,
                    person.department,
                    '',
                    person.street,
                    `${person.zip_code || ''} ${person.city || ''}`.trim(),
                    person.country,
                ].filter(line => line !== null && line !== undefined);

                for (let i = 0; i < lines.length; i++) {
                    const line = lines[i];

                    if (line.length === 0) {
                        textY -= fontSize + 2;
                    } else {
                        const wrappedLines = await dispatch('wrapText', {
                            text: line,
                            font,
                            fontSize,
                            maxWidth: maxTextWidth
                        });
                        for (const wLine of wrappedLines) {
                            currentPage.drawText(wLine, {
                                x: rightColX,
                                y: textY,
                                size: fontSize,
                                font: (i === 0) ? fontBold : font,
                            });
                            textY -= fontSize + 2;
                        }
                    }
                }

                // Draw bio formatted HTML

                const lineSpacing = fontSize + 2;
                const extraSpacingAfterBio = lineSpacing * 2;
                if (person.bio) {
                    textY -= 10;
                    textY = await dispatch('drawFormattedHtml', {
                        text: person.bio,
                        x: rightColX,
                        y: textY,
                        maxWidth: maxTextWidth,
                        font: fontItalic,
                        page: currentPage,
                        size: fontSize,
                    });
                    textY -= extraSpacingAfterBio;
                }

                if (person.note) {
                    const noteFontSize = 9;
                    const notePadding = 4;
                    const topPadding = 6; // extra padding above the highlight
                    const maxNoteWidth = width - marginX * 2;  // full page width minus margins
                    const noteX = marginX;                      // start at left margin

                    // Ensure the note starts below the image bottom + padding
                    textY = Math.min(textY, startY - imageHeight - 10); // 10px gap below image

                    // Rough estimate for line count
                    const approxCharsPerLine = 60;
                    const lines = Math.ceil(person.note.length / approxCharsPerLine);
                    const lineHeight = noteFontSize * 1.2;
                    const noteHeight = lines * lineHeight + notePadding * 2;

                    // Adjust y position for the background to include top padding
                    currentPage.drawRectangle({
                        x: noteX - notePadding,
                        y: textY - noteHeight + notePadding - topPadding,
                        width: maxNoteWidth + notePadding * 2,
                        height: noteHeight + topPadding,
                        color: rgb(1, 1, 0.6), // light yellow highlight
                        opacity: 0.6,
                    });

                    // Draw italic note text inside the highlight, shifted down by topPadding
                    textY = await dispatch('drawFormattedHtml', {
                        text: person.note,
                        x: noteX,
                        y: textY - topPadding,
                        maxWidth: maxNoteWidth,
                        font: fontItalic,
                        page: currentPage,
                        size: noteFontSize,
                    });

                    // Add some spacing below the note block
                    textY -= 8;
                }



                y = textY - 20;
                //y = startY - totalHeightNeeded - 20; // next person's Y position
            }
        }
        //END PERSON BLOCK

        //START PROGRAMME BLOCK

        if (events.length > 0) {
            currentPage = pdfDoc.addPage();
            y = currentPage.getSize().height - headerHeight - topMargin;
            const groupedEvents = await dispatch('groupEventsByDate', events);
            const sortedDates = Object.keys(groupedEvents).sort(); // chronological order
            const printedSessions = new Set();
            const sessionFontSize = 10;
            const presentationFontSize = 9;

            for (const date of sortedDates) {
                // Draw date header
                const dateObj = new Date(date);
                const dateHeader = dateObj.toLocaleDateString(undefined, {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });

                currentPage.drawText(dateHeader, {
                    x: 40,
                    y,
                    size: 12,
                    font: fontBold,
                });
                y -= 30;

                const eventsForDate = groupedEvents[date].sort((a, b) => {
                    const aStart = new Date(
                        a.fav_type === 'session'
                            ? a.start_time ?? 0
                            : a.session?.start_time ?? 0
                    );
                    const bStart = new Date(
                        b.fav_type === 'session'
                            ? b.start_time ?? 0
                            : b.session?.start_time ?? 0
                    );

                    return aStart - bStart;
                });

                for (const event of eventsForDate) {
                    let session;
                    let presentationsToPrint = [];

                    if (event.fav_type === 'session') {
                        session = event;
                        if (!session?.id || printedSessions.has(session.id)) continue;

                        // Sort presentations by start_time here
                        presentationsToPrint = (session.presentations || []).slice().sort(
                            (a, b) => new Date(a.start_time) - new Date(b.start_time)
                        );

                    } else if (event.fav_type === 'presentation') {
                        session = event.session;
                        if (!session?.id || printedSessions.has(session.id)) continue;

                        // Only include presentations from the same session that are also in the events array
                        presentationsToPrint = eventsForDate.filter(ev =>
                            ev.fav_type === 'presentation' &&
                            ev.session?.id === session.id
                        ).sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
                    } else {
                        continue; // skip unknown types
                    }

                    printedSessions.add(session.id);

                    // Render session header with HTML support, including code
                    // Line 1: Session Type (e.g., "Symposium")
                    currentPage.drawText(session.session_type_text || 'Session', {
                        x: 50,
                        y,
                        size: sessionFontSize,
                        font: fontBold,
                    });
                    y -= sessionFontSize + 4;

// Line 2: Time and Location
                    const sessionTimeRange = `${new Date(session.start_time).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                    })} - ${new Date(session.end_time).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                    })}`;

                    const sessionLocation = session.location_name ? ` · ${session.location_name}` : '';
                    const timeLocationText = `${sessionTimeRange}${sessionLocation}`;

                    currentPage.drawText(timeLocationText, {
                        x: 50,
                        y,
                        size: sessionFontSize - 1,
                        font,
                    });
                    y -= sessionFontSize + 3;

// Line 3: Code - Title (formatted HTML)
                    const codeAndTitle = `${session.code ? `${session.code} - ` : ''}${session.title || 'Untitled Session'}`;
                    y = await dispatch('drawFormattedHtml', {
                        text: codeAndTitle,
                        x: 50,
                        y,
                        maxWidth: width - 100,
                        font,
                        page: currentPage,
                        size: sessionFontSize,
                    });
                    y -= 5;

// Line 4: Chairs (if any)
                    if (session.chair?.length > 0) {
                        for (const chair of session.chair) {
                            const chairText = `Chair: ${chair.display_name}${chair.function ? ` (${chair.function})` : ''}`;
                            currentPage.drawText(chairText, {
                                x: 50,
                                y,
                                size: presentationFontSize,
                                font,
                            });
                            y -= presentationFontSize + 2;
                        }
                        y -= 5;
                    }

// Padding before presentations
                    y -= 10;


                    // Draw presentations
                    for (const pres of presentationsToPrint) {
                        const presTime = `${new Date(pres.start_time).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                        })} - ${new Date(pres.end_time).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                        })}`;

                        const presTitle = `${pres.title || 'Untitled Presentation'}${pres.code ? ` - ${pres.code}` : ''}`;

                        // Save original Y before drawing title
                        const titleY = y;

                        // Left column: presentation title
                        const newY = await dispatch('drawFormattedHtml', {
                            text: presTitle,
                            x: 60,
                            y: titleY,
                            maxWidth: (width - 100) * 0.7,
                            font: fontBold,
                            page: currentPage,
                            size: presentationFontSize,
                        });

                        // Right column: presentation time aligned with titleY
                        currentPage.drawText(presTime, {
                            x: 60 + (width - 100) * 0.7 + 10,
                            y: titleY,
                            size: presentationFontSize,
                            font,
                        });

                        // Move y down for next item
                        y = newY - 5;

                        // Draw speakers
                        if (pres.speakers?.length > 0) {
                            for (const sp of pres.speakers) {
                                const speakerText = `    ${sp.display_name}${sp.function ? ` (${sp.function})` : ''}`;
                                currentPage.drawText(speakerText, {
                                    x: 65,
                                    y,
                                    size: presentationFontSize - 1,
                                    font,
                                });
                                y -= (presentationFontSize - 1) + 2;
                            }
                        }

                        y -= 5;

                        // Inside your presentations loop, after speakers drawing & y -= 5

                        if (pres.note) {
                            const noteFontSize = 8;
                            const notePadding = 3;
                            const topPadding = 5;
                            const maxNoteWidth = (width - 100) * 0.9;
                            const noteX = 65; // slightly indented like speakers

                            const approxCharsPerLine = 50;
                            const linesCount = Math.ceil(pres.note.length / approxCharsPerLine);
                            const lineHeight = noteFontSize * 1.2;
                            const noteHeight = linesCount * lineHeight + notePadding * 2 + topPadding;

                            const spaceCheck = await dispatch('ensureSpace', {
                                currentY: y,
                                requiredHeight: noteHeight + 8,
                                pageHeight: height,
                                pdfDoc,
                            });

                            if (spaceCheck) {
                                currentPage = spaceCheck.page;
                                y = spaceCheck.y;
                            }

                            // Draw background highlight
                            currentPage.drawRectangle({
                                x: noteX - notePadding,
                                y: y - noteHeight + notePadding,
                                width: maxNoteWidth + notePadding * 2,
                                height: noteHeight,
                                color: rgb(1, 1, 0.6),
                                opacity: 0.6,
                            });

                            // Draw note text
                            y = await dispatch('drawFormattedHtml', {
                                text: pres.note,
                                x: noteX,
                                y: y - topPadding,
                                maxWidth: maxNoteWidth,
                                font: fontItalic,
                                page: currentPage,
                                size: noteFontSize,
                            });

                            y -= 10; // spacing after note
                        }


                        // Page space check
                        const spaceCheck = await dispatch('ensureSpace', {
                            currentY: y,
                            requiredHeight: 60,
                            pageHeight: height,
                            pdfDoc,
                        });

                        if (spaceCheck) {
                            currentPage = spaceCheck.page;
                            y = spaceCheck.y;
                        }
                    }


                    // After presentations loop and before y -= 20 spacing

                    if (session.note) {
                        const noteFontSize = 9;
                        const notePadding = 4;
                        const topPadding = 6;
                        const maxNoteWidth = width - 100; // matching margins you use elsewhere
                        const noteX = 50;

                        // Ensure space for note
                        const approxCharsPerLine = 60;
                        const linesCount = Math.ceil(session.note.length / approxCharsPerLine);
                        const lineHeight = noteFontSize * 1.2;
                        const noteHeight = linesCount * lineHeight + notePadding * 2 + topPadding;

                        const spaceCheck = await dispatch('ensureSpace', {
                            currentY: y,
                            requiredHeight: noteHeight + 10, // extra padding below note
                            pageHeight: height,
                            pdfDoc,
                        });

                        if (spaceCheck) {
                            currentPage = spaceCheck.page;
                            y = spaceCheck.y;
                        }

                        // Draw background highlight for note
                        currentPage.drawRectangle({
                            x: noteX - notePadding,
                            y: y - noteHeight + notePadding,
                            width: maxNoteWidth + notePadding * 2,
                            height: noteHeight,
                            color: rgb(1, 1, 0.6),
                            opacity: 0.6,
                        });

                        // Draw the note text with HTML formatting support
                        y = await dispatch('drawFormattedHtml', {
                            text: session.note,
                            x: noteX,
                            y: y - topPadding,
                            maxWidth: maxNoteWidth,
                            font: fontItalic,
                            page: currentPage,
                            size: noteFontSize,
                        });

                        y -= 15; // spacing after note
                    }



                    y -= 20; // spacing after presentations list
                }

                y -= 40; // space between date groups
            }
        }
        //END PROGRAMME BLOCK

        //START INDUSTRY BLOCK
        if (industryList.length > 0) {
            const footerHeight = 40;

            const blockSpacingY = 40;
            const pageWidth = width;
            const pageHeight = height;
            const contentWidth = pageWidth - marginX * 2;
            const industryTextWidth = contentWidth / 2;
            const industryLogoWidth = contentWidth - industryTextWidth - 20;
            const textXStart = marginX;
            const industrylogoX = marginX + industryTextWidth + 20;

            currentPage = pdfDoc.addPage();
            y = pageHeight - headerHeight - topMargin;

            for (const industry of industryList) {
                // ----------- Estimate dynamic height before rendering -------------
                let estimatedHeight = 0;

                // Estimate logo height
                if (industry.imageBase64) {
                    const isJpg = industry.imageBase64.includes('/9j/');
                    const isPng = industry.imageBase64.includes('iVBOR');
                    const cleanBase64 = industry.imageBase64.replace(/^data:.*?;base64,/, '');

                    try {
                        const img = isJpg
                            ? await pdfDoc.embedJpg(cleanBase64)
                            : isPng
                                ? await pdfDoc.embedPng(cleanBase64)
                                : null;

                        if (img) {
                            const scale = Math.min(industryLogoWidth / img.width, 1);
                            const logoHeight = img.height * scale;
                            estimatedHeight = Math.max(estimatedHeight, logoHeight);
                        }
                    } catch {
                        estimatedHeight = Math.max(estimatedHeight, 100);
                    }
                }

                // Estimate text block height
                let textHeight = 0;
                textHeight += 20; // industry name
                if (industry.description) textHeight += 60;

                const contactLines = [
                    industry.street,
                    `${industry.zip_code || ''} ${industry.city || ''}`.trim(),
                    [industry.state, industry.country].filter(Boolean).join(', '),
                    industry.email,
                    industry.phone,
                    industry.homepage ? `Homepage: ${industry.homepage}` : '',
                ].filter(Boolean);
                textHeight += contactLines.length * 12 + 10;

                if (industry.locations?.length) {
                    textHeight += 14 + industry.locations.length * 11;
                }

                if (industry.note) {
                    const noteLines = Math.ceil(industry.note.length / 60);
                    textHeight += 6 + noteLines * 9 * 1.2 + 8;
                }

                estimatedHeight = Math.max(estimatedHeight, textHeight);
                estimatedHeight += 30; // padding + separator + spacing

                // Ensure space available before rendering
                const spaceCheck = await dispatch('ensureSpace', {
                    currentY: y,
                    requiredHeight: estimatedHeight,
                    pageHeight: pageHeight - footerHeight,
                    pdfDoc,
                });

                if (spaceCheck) {
                    currentPage = spaceCheck.page;
                    y = spaceCheck.y;
                }

                // ----------- Start actual rendering ------------------

                let logoBottomY = y - estimatedHeight;

                if (industry.imageBase64) {
                    try {
                        let industryImg;
                        const cleanBase64 = industry.imageBase64.replace(/^data:.*?;base64,/, '');
                        if (industry.imageBase64.includes('/9j/')) {
                            industryImg = await pdfDoc.embedJpg(cleanBase64);
                        } else if (industry.imageBase64.includes('iVBOR')) {
                            industryImg = await pdfDoc.embedPng(cleanBase64);
                        }

                        const scale = Math.min(industryLogoWidth / industryImg.width, 1);
                        const logoHeight = industryImg.height * scale;

                        currentPage.drawImage(industryImg, {
                            x: industrylogoX,
                            y: y - logoHeight,
                            width: industryLogoWidth,
                            height: logoHeight,
                        });

                        logoBottomY = y - logoHeight;
                    } catch (err) {
                        console.error('Invalid image data for industry:', industry.name);
                    }
                }

                let textY = y;

                currentPage.drawText(industry.name, {
                    x: textXStart,
                    y: textY,
                    size: 12,
                    font: fontBold,
                    maxWidth: industryTextWidth,
                });
                textY -= 20;

                if (industry.description) {
                    textY = await dispatch('drawFormattedHtml', {
                        text: industry.description,
                        x: textXStart,
                        y: textY,
                        maxWidth: industryTextWidth,
                        font,
                        page: currentPage,
                        size: 9,
                    });
                    textY -= 8;
                }

                for (const line of contactLines) {
                    currentPage.drawText(line, {
                        x: textXStart,
                        y: textY,
                        size: 9,
                        font,
                        maxWidth: industryTextWidth,
                    });
                    textY -= 12;
                }

                textY -= 10;

                if (industry.locations?.length) {
                    currentPage.drawText('Visit on Booth:', {
                        x: textXStart,
                        y: textY,
                        size: 9,
                        font: fontBold,
                    });
                    textY -= 14;

                    for (const loc of industry.locations) {
                        currentPage.drawText(`• ${loc.name}`, {
                            x: textXStart + 10,
                            y: textY,
                            size: 9,
                            font,
                            maxWidth: industryTextWidth - 10,
                        });
                        textY -= 11;
                    }
                }

                if (industry.note) {
                    const noteFontSize = 9;
                    const notePadding = 4;
                    const topPadding = 6;
                    const maxNoteWidth = width - marginX * 2;
                    const noteX = marginX;

                    const approxCharsPerLine = 60;
                    const lines = Math.ceil(industry.note.length / approxCharsPerLine);
                    const lineHeight = noteFontSize * 1.2;
                    const noteHeight = lines * lineHeight + notePadding * 2;

                    currentPage.drawRectangle({
                        x: noteX - notePadding,
                        y: textY - noteHeight + notePadding - topPadding,
                        width: maxNoteWidth + notePadding * 2,
                        height: noteHeight + topPadding,
                        color: rgb(1, 1, 0.6),
                        opacity: 0.6,
                    });

                    textY = await dispatch('drawFormattedHtml', {
                        text: industry.note,
                        x: noteX,
                        y: textY - topPadding,
                        maxWidth: maxNoteWidth,
                        font: fontItalic,
                        page: currentPage,
                        size: noteFontSize,
                    });

                    textY -= 8;
                }

                const blockBottomY = Math.min(logoBottomY, textY);
                const lineY = blockBottomY - 10;

                currentPage.drawLine({
                    start: { x: marginX, y: lineY },
                    end: { x: pageWidth - marginX, y: lineY },
                    thickness: 1,
                    color: rgb(0.7, 0.7, 0.7),
                });

                y = lineY - blockSpacingY;
            }
        }

        //END INDUSTRY BLOCK

        let returnPdf = await dispatch('applyHeaderAndFooter', {pdfDoc: pdfDoc, font: font, logo: '/assets/canvas-bg.png', text: text})
        return returnPdf;
    },

    async applyHeaderAndFooter(context, {pdfDoc, font, logo, text}) {
        const headerHeight = 60;
        const margin = 20;

        // 1. Load the logo and embed it (optional - handle errors gracefully)
        let logoImage = null;
        let logoScaledWidth = 50;
        let logoScaledHeight = 0;
        
        try {
            const logoBytes = await fetch(logo).then(res => {
                if (!res.ok) throw new Error('Logo not found');
                return res.arrayBuffer();
            });
            logoImage = await pdfDoc.embedPng(logoBytes);
            logoScaledHeight = (logoImage.height / logoImage.width) * logoScaledWidth;
        } catch (err) {
            console.warn('Header logo not loaded, continuing without it:', err.message);
        }

        // 2. Set your header text
        const headerText = text;
        const headerFontSize = 14;

        const pages = pdfDoc.getPages();


        // 3. Loop through all pages to draw the header
        for (let i = 1; i < pages.length; i++) {
            let page = pages[i]
            const {width, height} = page.getSize();

            // Draw header text (left)
            page.drawText(headerText, {
                x: margin,
                y: height - headerFontSize - 20,
                size: headerFontSize,
                font,
                color: rgb(0, 0, 0),
            });

            // Draw logo (right) - only if loaded successfully
            if (logoImage) {
                page.drawImage(logoImage, {
                    x: width - logoScaledWidth - margin,
                    y: height - logoScaledHeight - 15,
                    width: logoScaledWidth,
                    height: logoScaledHeight,
                });
            }

            // Optional: Draw a light line below header
            page.drawLine({
                start: {x: 0, y: height - headerHeight},
                end: {x: width, y: height - headerHeight},
                thickness: 0.5,
                color: rgb(0.8, 0.8, 0.8),
            });


            const footerSize = 8
            const text = `Page ${i + 1} of ${pages.length}`;
            const textWidth = font.widthOfTextAtSize(text, footerSize);

            page.drawText(text, {
                x: (width - textWidth) / 2, // center horizontally
                y: 20, // 20px from bottom
                size: footerSize,
                font,
                color: rgb(0.5, 0.5, 0.5), // subtle gray
            });
        }
        return pdfDoc;
    },


    //apply only header
    async applyHeader(context, {pdfDoc, font}) {
        const headerHeight = 60;
        const margin = 20;
        // 1. Load the logo and embed it
        const logoBytes = await fetch('/assets/canvas-bg.png').then(res => res.arrayBuffer());
        const logoImage = await pdfDoc.embedPng(logoBytes);
        const logoScaledWidth = 50; // or whatever fits nicely in 60px height
        const logoScaledHeight = (logoImage.height / logoImage.width) * logoScaledWidth;
        // 2. Set your header text
        const headerText = 'FENS 2024 Export';
        const headerFontSize = 14;
        const pages = pdfDoc.getPages();

        // 3. Loop through all pages to draw the header
        for (let i = 1; i < pages.length; i++) {
            let page = pages[i]
            const {width, height} = page.getSize();

            // Draw header text (left)
            page.drawText(headerText, {
                x: margin,
                y: height - headerFontSize - 20,
                size: headerFontSize,
                font,
                color: rgb(0, 0, 0),
            });

            // Draw logo (right)
            page.drawImage(logoImage, {
                x: width - logoScaledWidth - margin,
                y: height - logoScaledHeight - 15,
                width: logoScaledWidth,
                height: logoScaledHeight,
            });

            // Optional: Draw a light line below header
            page.drawLine({
                start: {x: 0, y: height - headerHeight},
                end: {x: width, y: height - headerHeight},
                thickness: 0.5,
                color: rgb(0.8, 0.8, 0.8),
            });
        }
        return pdfDoc;
    },
    //apply only footer
    async applyFooter(context, {pdfDoc, font}) {
        const pages = pdfDoc.getPages();
        const fontSize = 10;

        for (let i = 0; i < pages.length; i++) {
            const page = pages[i];
            const {width} = page.getSize();

            const text = `Page ${i + 1} of ${pages.length}`;
            const textWidth = font.widthOfTextAtSize(text, fontSize);

            page.drawText(text, {
                x: (width - textWidth) / 2, // center horizontally
                y: 20, // 20px from bottom
                size: fontSize,
                font,
                color: rgb(0.5, 0.5, 0.5), // subtle gray
            });
        }
        return pdfDoc
    }


}

const mutations = {}
const getters = {}

export const pdfgen = {
    namespaced,
    state,
    getters,
    mutations,
    actions
}

